import axios from 'axios';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';
import {
  footerFacebook,
  footerInstagram,
  footerLinkedin,
  footerTwitter,
  footerYoutube,
  FooterDesign
  // FooterDesignMobile
} from '../constants/asset-constants';
import { parseGraphqlError } from '../utilities/parse-graphql-error';
import { useSendAlert } from '../utilities/send-alert';
import { isDisposableEmail } from '../utilities/is-disposable-email';
import BusyOverlay from './busy-overlay';
import { TopShipWhiteLogo } from './new-home-page/asset-constants';
import { supportArray } from './new-home-page/constants';

const NewFooter = () => {
  const [submitEmailLoading, setSubmitEmailLoading] = useState(false);
  const [formLoadedAt] = useState(Date.now());
  const sendAlert = useSendAlert();
  const SLACK_URL = process.env.REACT_APP_SLACK_API;

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    resetForm
  } = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      company_url: ''
    },
    validationSchema: yup.object().shape({
      email: yup
        .string()
        .email('Please enter a valid email')
        .test('no-dot-spam', 'Please enter a valid email', value => {
          if (!value) return true;
          const username = value.split('@')[0];
          const dotCount = (username.match(/\./g) || []).length;
          return dotCount <= 2;
        })
        .test(
          'no-disposable-email',
          'Please enter a valid permanent email',
          value => !value || !isDisposableEmail(value)
        )
        .required('Please enter an email'),
      firstName: yup
        .string()
        .min(2, 'Name must be at least 2 characters')
        .max(50, 'Name must be at most 50 characters')
        .matches(
          /^[a-zA-ZÀ-ÖØ-öø-ÿ]+([ '-][a-zA-ZÀ-ÖØ-öø-ÿ]+)*$/,
          'Please enter a valid name'
        )
        .matches(/[aeiouyAEIOUY]/, 'Please enter a valid name')
        .test(
          'no-consonant-clusters',
          'Please enter a valid name',
          value =>
            !value ||
            !/[bcdfghjklmnpqrstvwxzBCDFGHJKLMNPQRSTVWXZ]{4,}/.test(value)
        )
        .test(
          'no-consecutive-repeats',
          'Please enter a valid name',
          value => !value || !/(.)\1\1/.test(value)
        )
        .test(
          'no-q-consonant',
          'Please enter a valid name',
          value =>
            !value ||
            !/[qQ][bcdfghjklmnpqrstvwxzBCDFGHJKLMNPQRSTVWXZ]/.test(value)
        )
        .required('Please enter a name')
    }),
    onSubmit: async (values, { resetForm: formReset }) => {
      // 1. Honeypot check: to ensure the form is filled by a humn n not a bot
      if (values.company_url) {
        sendAlert("Thanks for subscribing! We'll keep you updated.", 'success');
        if (formReset) {
          formReset();
        } else {
          resetForm();
        }
        return;
      }

      // 2. Timing check: bots submit almost instantaneously (< 2s)
      const secondsSinceLoad = (Date.now() - formLoadedAt) / 1000;
      if (secondsSinceLoad < 2) {
        sendAlert("Thanks for subscribing! We'll keep you updated.", 'success');
        if (formReset) {
          formReset();
        } else {
          resetForm();
        }
        return;
      }

      setSubmitEmailLoading(true);

      try {
        const response = await axios.post(SLACK_URL, {
          text: `Email: ${values.email} First Name: ${values.firstName}`,
          channel: 'marketing'
        });

        if (response.data) {
          setSubmitEmailLoading(false);
          sendAlert(
            "Thanks for subscribing! We'll keep you updated.",
            'success'
          );
          resetForm();
        }
      } catch (error) {
        setSubmitEmailLoading(false);

        sendAlert(parseGraphqlError(error), 'error');

        throw error;
      }
    }
  });

  return (
    // <div className='footer__container-main'>

    //   <img
    //     src={FooterDesign}
    //     alt='illustration'
    //     className='footer__illustration'
    //   />
    <footer className='new-home-page__footer-container'>
      {/* <img
        src={FooterDesignMobile}
        alt='illustration'
        className='footer__illustration'
      /> */}
      <img
        src={FooterDesign}
        alt='illustration'
        className='footer__illustration'
      />
      <div className='footer-content-items'>
        <div className='help-wrap mobile-version'>
          {supportArray.map(support => {
            return (
              <div className='help-item'>
                <div className='mb-1'>
                  <img
                    style={{
                      paddingRight: '4px'
                    }}
                    src={support.icon}
                    alt='support logo'
                  />
                  <span>{support.title}</span>
                </div>
                <a
                  style={{
                    textDecoration: 'underline'
                  }}
                  href={`${
                    support.label === '02013302594'
                      ? `tel:${support.label}`
                      : support.label === '+2349080777728'
                        ? `https://wa.me/${support.label}`
                        : `mailto:${support.label}`
                  }`}
                  target='_blank'
                  rel='noreferrer'
                >
                  {support.label}
                </a>
              </div>
            );
          })}
          <hr
            style={{
              border: '1px solid rgba(255, 255, 255, 0.7)',
              marginBottom: '30px'
            }}
          />
        </div>

        <div className='footer-content-item'>
          <div className='content-item-columns'>
            <div className='column-items topship-text'>
              <div className='help-item web-version'>
                <div className='mb-1'>
                  <img
                    style={{
                      paddingRight: '4px'
                    }}
                    src={supportArray[0].icon}
                    alt='support logo'
                  />
                  <span>{supportArray[0].title}</span>
                </div>
                <a
                  href={`tel:${supportArray[0].label}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  {supportArray[0].label}
                </a>
              </div>
              <img
                className='footer-logo'
                src={TopShipWhiteLogo}
                alt='topship logo'
              />
              <p className='topship-footer-text'>
                Topship helps you send and receive items from your doorstep to
                anywhere in the world
              </p>
            </div>
            <div className='column-items'>
              <div className='help-item web-version'>
                <div className='mb-1'>
                  <img
                    style={{
                      paddingRight: '4px'
                    }}
                    src={supportArray[1].icon}
                    alt='email logo'
                  />
                  <span>{supportArray[1].title}</span>
                </div>
                <a
                  href={`mailto:${supportArray[1].label}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  {supportArray[1].label}
                </a>
              </div>
              <ul>
                <li className='column-item column-item-header'>
                  International Shipping
                </li>
                <li>
                  <a href='/ng'>
                    Ship from Nigeria to UK, USA, {'&'} 150+ cities
                  </a>
                </li>
                <li>
                  <a href='/us/ng'>Ship from the US to Nigeria</a>
                </li>
                <li>
                  <a href='/uk/ng'>Ship from the UK to Nigeria</a>
                </li>
              </ul>
            </div>
            <div className='column-items'>
              <div className='help-item web-version'>
                <div className='mb-1'>
                  <img
                    style={{
                      paddingRight: '4px'
                    }}
                    src={supportArray[2].icon}
                    alt='whatsapp logo'
                  />
                  <span>{supportArray[2].title}</span>
                </div>
                <a
                  href={`https://wa.me/${supportArray[2].label}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  {supportArray[2].label}
                </a>
              </div>
              <ul>
                <li className='column-item column-item-header'>
                  Inter-state Delivery
                </li>
                <li>
                  <a href='/lagos'>Lagos to other States</a>
                </li>
                <li>
                  <a href='/abuja'>Abuja to other States</a>
                </li>
                <li>
                  <a href='/ibadan'>Ibadan to other States</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <hr
          style={{
            border: '1px solid rgba(255, 255, 255, 0.7)',
            marginBlock: '30px'
          }}
        />
        <div className='footer-content-item-newsletter  mb-5'>
          <div className='newsletter-content'>
            <div
              className='column-item column-item-header'
              style={{
                textAlign: 'left',
                fontSize: '32px',
                lineHeight: '38px',
                fontWeight: '500'
              }}
            >
              Stay up to date
              <br />
              with our newsletter
            </div>

            <form className='' onSubmit={handleSubmit}>
              <div
                style={{
                  opacity: 0,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: 0,
                  width: 0,
                  zIndex: -1,
                  overflow: 'hidden',
                  pointerEvents: 'none'
                }}
                aria-hidden='true'
              >
                <input
                  type='text'
                  name='company_url'
                  tabIndex={-1}
                  autoComplete='off'
                  value={values.company_url}
                  onChange={handleChange}
                />
              </div>
              <div className='input-group'>
                <div className='input-field name-input'>
                  <input
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.firstName}
                    type='text'
                    placeholder='Enter your first name'
                    name='firstName'
                    className=''
                  />
                  {touched.firstName && errors.firstName && (
                    <span className='validation-error text-center footer-error-message'>
                      {errors.firstName}
                    </span>
                  )}
                </div>
                <div className='input-field email-input'>
                  <input
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    type='email'
                    placeholder='Enter your email'
                    name='email'
                    className=''
                  />
                  {touched.email && errors.email && (
                    <span className='validation-error text-center footer-error-message'>
                      {errors.email}
                    </span>
                  )}
                </div>
              </div>
              <button
                type='submit'
                disabled={errors.email || errors.firstName ? true : false}
                className='footer-button'
              >
                Subscribe
              </button>
            </form>
          </div>
          <div className='footer-socials'>
            <a
              href={`https://web.facebook.com/topshipafrica?_rdc=1&_rdr`}
              rel='noreferrer'
              target='_blank'
            >
              <img
                className='facebook-icon'
                src={footerFacebook}
                alt='facebook logo'
              />
            </a>
            <a
              href='https://twitter.com/topshipafrica'
              rel='noreferrer'
              target='_blank'
            >
              <img src={footerTwitter} alt='twitter logo' />
            </a>
            <a
              href='https://www.linkedin.com/company/topshipafrica/'
              rel='noreferrer'
              target='_blank'
            >
              <img
                className='linkedin-icon'
                src={footerLinkedin}
                alt='linkedin logo'
              />
            </a>
            <a
              href='https://www.instagram.com/topshipafrica/'
              rel='noreferrer'
              target='_blank'
            >
              <img src={footerInstagram} alt='instagram logo' />
            </a>
            <a
              href='https://www.youtube.com/channel/UC8q9ckTRsIfnE1srJOWPnug'
              rel='noreferrer'
              target='_blank'
            >
              <img src={footerYoutube} alt='youtube logo' />
            </a>
          </div>
        </div>
        <hr
          style={{
            // border: '1px solid rgba(255, 255, 255, 0.7)',
            marginBlock: '30px'
          }}
        />
        <div className='footer-content-item'>
          <div className='rights-reserved-item mb-3'>
            <p>
              &copy; {new Date().getFullYear()} Topship Africa. All rights
              reserved.
            </p>
          </div>
          <div className='rights-reserved-items'>
            <ul className='rights-reserved-contents'>
              <li>
                <a href='tel:+2349080777728' target='_blank' rel='noreferrer'>
                  Contact&#8599;
                </a>
              </li>
              <li>
                <a
                  href='https://api.whatsapp.com/send?phone=2349080777728'
                  target='_blank'
                  rel='noreferrer'
                >
                  WhatsApp&#8599;
                </a>
              </li>
              <li className='terms-privacy-cookies'>
                <a
                  target='_blank'
                  rel='noreferrer'
                  href='https://ship.topship.africa/terms'
                >
                  Terms&#8599;
                </a>
              </li>
              <li>
                <a
                  target='_blank'
                  rel='noreferrer'
                  href='https://ship.topship.africa/terms'
                >
                  Privacy&#8599;
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <BusyOverlay loading={submitEmailLoading} />
    </footer>
    // </div>
  );
};

export default NewFooter;
